import { useEffect, useState } from 'react';
import { useI18n } from '@/shared/i18n/useT';
import type { Translate } from '@/shared/i18n';
import { PageHeader } from '@/shared/components/PageHeader';
import { supabase } from '@/lib/supabase';

interface Booking { id: string; startsAt: string; status: string; timezone: string; eventName: string; pageTitle: string; }

function bookingStatusLabel(status: string, t: Translate): string {
  switch (status) {
    case 'pending': return t('calendarUi.bookingStatus.pending');
    case 'confirmed': return t('calendarUi.bookingStatus.confirmed');
    case 'completed': return t('calendarUi.bookingStatus.completed');
    case 'cancelled': return t('calendarUi.bookingStatus.cancelled');
    case 'no_show': return t('calendarUi.bookingStatus.no_show');
    default: return status;
  }
}

export function MyBookings() {
  const { t, formatLocale } = useI18n();
  const [bookings, setBookings] = useState<Booking[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    let active = true;
    void (async () => {
      try {
        const session = (await supabase.auth.getSession()).data.session;
        if (!session?.access_token) throw new Error('session-required');
        const response = await fetch('/api/bookings/mine', { headers: { Authorization: `Bearer ${session.access_token}` } });
        if (!response.ok) throw new Error('bookings-load');
        const payload = await response.json() as { bookings?: readonly Booking[] };
        const next = [...(payload.bookings ?? [])];
        if (active) setBookings(next);
      } catch { if (active) setError(true); }
      finally { if (active) setLoading(false); }
    })();
    return () => { active = false; };
  }, []);

  return <>
    <PageHeader title={t('client.bookings.title')} description={t('client.bookings.description')} />
    {loading ? <p role="status" className="text-sm text-muted">{t('common.loading')}</p> : error ? <p role="alert" className="rounded-token border border-danger p-4 text-sm text-danger">{t('calendarUi.loadError')}</p> : bookings.length === 0 ? <p className="rounded-token border border-border p-4 text-sm text-muted">{t('calendarUi.noBookings')}</p> : <ul className="space-y-3">{bookings.map((booking) => <li key={booking.id} className="rounded-token border border-border bg-surface-2 p-4"><div className="font-medium">{new Intl.DateTimeFormat(formatLocale, { dateStyle: 'medium', timeStyle: 'short', timeZone: booking.timezone }).format(new Date(booking.startsAt))}</div><div className="mt-1 text-sm text-muted">{bookingStatusLabel(booking.status, t)}</div><div className="mt-1 text-xs text-muted">{booking.eventName} · {booking.pageTitle}</div></li>)}</ul>}
  </>;
}
